import {
  ACCENT_COLOR_TYPE_BY_BONUS,
  acceptModeType,
  BONUS_DETAILS_KEYS,
  BONUSES_TYPES_FOR_IMAGES,
  freeSpinStates,
  progressBarStates
} from '../../../constants';
import prepareBonusDetailsList from '../../../utils/prepareBonusDetailsList';

const getBonusDetailsList = ({ bonusData, t, f, getCurrencySymbol }) => {
  const {
    gameName,
    freespinCount: freeSpinCount,
    currency,
    outputMaxTotal,
    expireTime,
    state,
    freespinEndTime = {}
  } = bonusData;

  const DATE_FORMATS_TYPES = f.getFormats();

  return prepareBonusDetailsList([
    {
      name: t('web.personaloffice.bonuses.game'),
      value: gameName,
      isDisplay: true
    },
    {
      name: t('web.personaloffice.bonuses.free.spins'),
      value: `${freeSpinCount} ${t('bonuses.details.freeSpins')}${
        currency ? `, ${getCurrencySymbol(currency)}` : ''
      }`,
      isDisplay: true
    },
    {
      name: t('web.personaloffice.bonuses.required.wager'),
      value: `${outputMaxTotal}${currency ? ` ${currency}` : ''}`,
      isDisplay: outputMaxTotal > 0,
      isBonusAmount: true
    },
    {
      key: BONUS_DETAILS_KEYS.DATE,
      name: t('web.personaloffice.bonuses.expiry.date'),
      value: f
        .getDateTime({ date: expireTime.ms })
        ?.toFormat(DATE_FORMATS_TYPES.BONUSES_DATE_FORMAT),
      isDisplay:
        state === freeSpinStates.FREESPIN_ACTVATED ||
        state === freeSpinStates.FREESPIN_ACCEPT_REQUEST ||
        state === freeSpinStates.FREESPIN_RELEASED ||
        state === freeSpinStates.FREESPIN_POTENCIAL
    },
    {
      key: BONUS_DETAILS_KEYS.DATE,
      name: t('web.personaloffice.bonuses.complited'),
      value: f
        .getDateTime({
          date: freespinEndTime?.sec * 1000 || freespinEndTime?.sec
        })
        ?.toFormat(DATE_FORMATS_TYPES.BONUSES_DATE_FORMAT),
      isDisplay: state === freeSpinStates.FREE_SPIN_USED
    },
    {
      key: BONUS_DETAILS_KEYS.DATE,
      name: t('web.personaloffice.bonuses.expired.date'),
      value: f
        .getDateTime({ date: expireTime.ms })
        ?.toFormat(DATE_FORMATS_TYPES.BONUSES_DATE_FORMAT),
      isDisplay:
        state === freeSpinStates.FREESPIN_INCOME_NOT_ACHIEVED ||
        state === freeSpinStates.FREESPIN_EXPIRED
    }
  ]);
};

const getProgressBarStatus = ({ state, outputBetTotal }) => {
  if (state === freeSpinStates.FREESPIN_WAGERING) {
    return progressBarStates.ACTIVE;
  }
  if (outputBetTotal === 0 && state === freeSpinStates.FREESPIN_PROPOSED) {
    return progressBarStates.NOT_ACTIVE;
  }
  if (outputBetTotal > 0 && state === freeSpinStates.FREESPIN_PROPOSED) {
    return progressBarStates.DISABLED;
  }
  if (
    state === freeSpinStates.FREESPIN_INCOME_NOT_ACHIEVED ||
    state === freeSpinStates.FREE_SPIN_USED ||
    state === freeSpinStates.FREESPIN_EXPIRED
  ) {
    return progressBarStates.EXPIRED;
  }

  return '';
};

export const normalizeFreeSpinBonusDetails = ({
  bonusData,
  userData,
  t,
  f,
  getCurrencySymbol
}) => {
  const {
    state,
    outputBetTotal,
    outputMaxTotal,
    acceptMode,
    freespinId,
    expireTime,
    maxActivationTime,
    freespinName
  } = bonusData;

  const { activeWallet = {}, wallets = [], feWallets } = userData;
  const { deposit: userBalance = 0 } = activeWallet;

  const isWagerAvailable = acceptModeType.WAGER.includes(acceptMode);
  const isShowWagerButton =
    state === freeSpinStates.FREESPIN_WAGERING && outputMaxTotal > 0;
  const isCanAccept = acceptModeType.ACCEPT.includes(acceptMode);
  const isReleased = state === freeSpinStates.FREESPIN_RELEASED;

  const isNoWallet = feWallets
    ? !feWallets.length
    : wallets.length === 0 &&
      userBalance <= 0 &&
      !isWagerAvailable &&
      !isCanAccept &&
      !isReleased;
  const isPotential = state === freeSpinStates.FREESPIN_POTENCIAL;
  const isActivation = state === freeSpinStates.FREESPIN_ACCEPT_REQUEST;
  const actualExpireTime =
    state === freeSpinStates.FREESPIN_RELEASED
      ? { ...expireTime, ms: expireTime?.sec * 1000 || 0 }
      : { ...maxActivationTime, ms: maxActivationTime?.sec * 1000 || 0 };

  return {
    ...bonusData,
    bonusId: freespinId,
    bonusName: freespinName,
    isWagerAvailable,
    isShowWagerButton,
    isCanAccept,
    isReleased,
    isNoWallet,
    isPotential,
    isActivation,
    isShowButton:
      isActivation ||
      isCanAccept ||
      isReleased ||
      isPotential ||
      isWagerAvailable ||
      isShowWagerButton,
    buttonType: (() => {
      if (isPotential || isNoWallet) {
        return 'deposit';
      }
      if (isWagerAvailable) {
        return outputBetTotal === 0 ? 'start' : 'resume';
      }
      if (isActivation) {
        return 'activation';
      }
      if (isCanAccept) {
        return 'accept';
      }
      if (isReleased) {
        return 'play';
      }
      if (isShowWagerButton) {
        return 'wager';
      }
      return '';
    })(),
    expireTime: actualExpireTime,
    bonusDetailsList: getBonusDetailsList({
      bonusData: { ...bonusData, expireTime: actualExpireTime },
      t,
      f,
      getCurrencySymbol
    }),
    isWagerEnabled:
      outputMaxTotal > 0 &&
      (state === freeSpinStates.FREESPIN_WAGERING ||
        state === freeSpinStates.FREESPIN_PROPOSED ||
        state === freeSpinStates.FREESPIN_INCOME_NOT_ACHIEVED ||
        state === freeSpinStates.FREESPIN_EXPIRED),
    progressBarStatus: getProgressBarStatus({ state, outputBetTotal }),
    isExpireTimeEnabled:
      state === freeSpinStates.FREESPIN_ACTVATED ||
      state === freeSpinStates.FREESPIN_ACCEPT_REQUEST ||
      state === freeSpinStates.FREESPIN_RELEASED,
    isCountdownTimerEnabled:
      state === freeSpinStates.FREESPIN_POTENCIAL ||
      state === freeSpinStates.FREESPIN_WAGERING ||
      (state === freeSpinStates.FREESPIN_PROPOSED && outputMaxTotal > 0),
    countdownTimerTitle: (() => {
      if (state === freeSpinStates.FREESPIN_POTENCIAL) {
        return t('web.personaloffice.bonuses.timer.time.to.deposit');
      }
      if (
        outputMaxTotal > 0 &&
        (state === freeSpinStates.FREESPIN_WAGERING ||
          state === freeSpinStates.FREESPIN_PROPOSED)
      ) {
        return t('web.personaloffice.bonuses.timer.hurry.up.to.wager');
      }
      if (state === freeSpinStates.FREESPIN_WAGERING) {
        return t('bonuses.countdownTimer.timeForActivity');
      }

      return '';
    })(),
    imageColorAccent:
      ACCENT_COLOR_TYPE_BY_BONUS[BONUSES_TYPES_FOR_IMAGES.CRM_FREE_SPIN_BONUS]
  };
};
